import { useContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import {
  analyzeGscSite,
  disconnectGsc,
  fetchGscAuthUrl,
  useGscSitesApi,
  useGscStatusApi
} from '@/common/api/modules/gsc'
import { GscAnalysis } from '@/common/api/params/gsc'
import { WidgetChoice } from '@/common/form/choice'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import AiButton from '@/components/view/atoms/button/AiButton'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import IconText from '@/components/view/atoms/icon/IconText'
import Selector, { SelectorSize } from '@/components/view/atoms/selector/Selector'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Line from '@/components/view/layouts/area/Line'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'

const DAYS_CHOICES: WidgetChoice<string | number>[] = [
  { name: '7日間', value: 7 },
  { name: '14日間', value: 14 },
  { name: '28日間', value: 28 },
  { name: '90日間', value: 90 }
]

const CONNECT_STEPS = [
  {
    title: 'Googleアカウントでログイン',
    desc: 'Google Search Consoleが紐づくGoogleアカウントを選択します。'
  },
  { title: 'アクセスを許可', desc: 'Google Search Consoleの読み取り権限のみを許可します。' },
  {
    title: 'サイトを選択して分析',
    desc: 'Google Search Consoleに登録されているサイトの検索データを分析します。'
  }
]

// CTR は高いほど良い / 掲載順位は小さいほど良い
const ctrColor = (ctr: number) =>
  ctr > 5 ? TextColor.SUCCESS : ctr > 2 ? TextColor.CAUTION : TextColor.SECONDARY
const positionColor = (pos: number) =>
  pos <= 10 ? TextColor.SUCCESS : pos <= 30 ? TextColor.CAUTION : TextColor.SECONDARY

const SummaryCard = ({ label, value }: { label: string; value: string }) => (
  <Area bgColor='secondary'>
    <AreaContent>
      <Text size={12} color={TextColor.SECONDARY}>
        {label}
      </Text>
      <Text size={24} weight='bold' color={TextColor.INFO}>
        {value}
      </Text>
    </AreaContent>
  </Area>
)

const MetricTable = ({
  title,
  firstLabel,
  rows
}: {
  title: string
  firstLabel: string
  rows: { key: string; clicks: number; impressions: number; ctr: number; position: number }[]
}) => (
  <Area>
    <Heading>{title}</Heading>
    <table className='w-full'>
      <thead>
        <TableRow head noBorder>
          <TableHeadItem type='thin'>
            <Text size={12} color={TextColor.SECONDARY}>
              {firstLabel}
            </Text>
          </TableHeadItem>
          {['クリック数', '表示回数', 'CTR', '掲載順位'].map((h) => (
            <TableHeadItem key={h} type='thin' width={88}>
              <Text size={12} color={TextColor.SECONDARY}>
                {h}
              </Text>
            </TableHeadItem>
          ))}
        </TableRow>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            <TableRowItem>
              <Text truncate>{r.key}</Text>
            </TableRowItem>
            <TableRowItem width={88}>
              <Text>{r.clicks.toLocaleString()}</Text>
            </TableRowItem>
            <TableRowItem width={88}>
              <Text>{r.impressions.toLocaleString()}</Text>
            </TableRowItem>
            <TableRowItem width={88}>
              <Text color={ctrColor(r.ctr)}>{r.ctr}%</Text>
            </TableRowItem>
            <TableRowItem width={88}>
              <Text color={positionColor(r.position)}>{r.position}</Text>
            </TableRowItem>
          </TableRow>
        ))}
      </tbody>
    </table>
  </Area>
)

const MediaGscLinkagePanel = ({ siteId }: { siteId: number }) => {
  const { showModal, hide } = useContext(ConfirmDialogContext)
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: status, mutate: mutateStatus } = useGscStatusApi({ site_id: siteId })
  const connected = !!status?.is_connected
  const hasCredentials = !!status?.has_credentials

  const { data: sitesData } = useGscSitesApi(connected ? { site_id: siteId } : undefined)
  const sites = sitesData?.sites ?? []

  const [authing, setAuthing] = useState(false)
  const [selectedSite, setSelectedSite] = useState('')
  const [days, setDays] = useState(28)
  const [analyzing, setAnalyzing] = useState(false)
  const [result, setResult] = useState<GscAnalysis | null>(null)
  const [resultError, setResultError] = useState<string | null>(null)
  const [kwFilter, setKwFilter] = useState('')

  // 未選択のうちは、前回の分析で選択された対象サイト（サーバー保存値）を既定値として使う
  const effectiveSite = selectedSite || status?.gsc_site_url || ''

  // OAuthリダイレクト（?gsc_connected=true）の検知
  useEffect(() => {
    if (searchParams.get('gsc_connected') === 'true' || searchParams.get('gsc_error')) {
      mutateStatus()
      searchParams.delete('gsc_connected')
      searchParams.delete('gsc_error')
      setSearchParams(searchParams, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAuth = async () => {
    setAuthing(true)
    try {
      const { auth_url } = await fetchGscAuthUrl(siteId)
      if (!auth_url) {
        setAuthing(false)
        return
      }
      window.open(auth_url, '_blank', 'width=600,height=700')
      // 認証完了をポーリングで監視（最大2分）
      const start = Date.now()
      const timer = setInterval(async () => {
        const s = await mutateStatus()
        if (s?.is_connected || Date.now() - start > 120000) {
          clearInterval(timer)
          setAuthing(false)
        }
      }, 3000)
    } catch {
      setAuthing(false)
    }
  }

  const handleDisconnect = () =>
    showModal({
      noText: 'キャンセル',
      yesText: '解除する',
      message: 'サーチコンソール連携を解除しますか？',
      onClickYes: async () => {
        await disconnectGsc(siteId)
        setResult(null)
        setSelectedSite('')
        mutateStatus()
        hide()
      }
    })

  const handleAnalyze = async () => {
    if (!effectiveSite) return
    setAnalyzing(true)
    setResult(null)
    setResultError(null)
    try {
      const res = await analyzeGscSite(siteId, effectiveSite, days)
      setResult(res.analysis)
    } catch {
      setResultError('GSC分析に失敗しました')
    } finally {
      setAnalyzing(false)
    }
  }

  // クレデンシャル未設定
  if (status && !hasCredentials) {
    return (
      <Area>
        <AreaContent>
          <Text size={14} color={TextColor.SECONDARY} align='center'>
            Google OAuthクレデンシャル（Client ID / Secret）がシステム設定に登録されていません。
          </Text>
          <Text size={12} color={TextColor.SECONDARY} align='center'>
            管理者にシステム設定での GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET
            設定を依頼してください。
          </Text>
        </AreaContent>
      </Area>
    )
  }

  // 未連携 → 連携案内
  if (!connected) {
    return (
      <Area>
        <Heading>認証の流れ</Heading>
        {CONNECT_STEPS.map((s, i) => (
          <div className='flex flex-col gap-2 px-6 py-2' key={i}>
            <ItemAlign key={i} gapX={8} align='center'>
              <Badge size={12} badgeType={BadgeType.SQUARE_PRIMARY}>
                {`STEP 0${i + 1}`}
              </Badge>
              <Text weight='bold'>{s.title}</Text>
            </ItemAlign>
            <Text size={12} tag='p'>
              {s.desc}
            </Text>
            {i < CONNECT_STEPS.length - 1 && <Line />}
          </div>
        ))}
        <div className='flex justify-center py-2'>
          <Button color={ButtonColor.PRIMARY} size='large' loading={authing} onClick={handleAuth}>
            {authing ? 'Googleに接続中...' : 'サーチコンソールの認証を行う'}
          </Button>
        </div>
      </Area>
    )
  }

  // 連携済み
  return (
    <div className='flex flex-col gap-6'>
      <Area>
        <Heading
          rightNode={
            <Button color={ButtonColor.SECONDARY} onClick={handleDisconnect}>
              連携を解除
            </Button>
          }
        >
          <IconText
            text='サーチコンソール連携済み'
            iconType={IconType.CHECK}
            color={IconColor.SUCCESS}
            textColor={TextColor.SUCCESS}
            direction='horizon'
          />
        </Heading>
        <AreaContent>
          <FormRow>
            <FormLabel>分析するサイト</FormLabel>
            <Selector
              name='gsc-site'
              size={SelectorSize.REMNANT}
              value={effectiveSite}
              choices={[
                { name: 'サイトを選択...', value: '' },
                ...sites.map((s) => ({
                  name: `${s.site_url}（${s.permission_level}）`,
                  value: s.site_url
                }))
              ]}
              onChange={(e) => setSelectedSite(e.target.value)}
            />
          </FormRow>
          <FormRow>
            <FormLabel>期間</FormLabel>
            <ItemAlign gapX={8} align='center'>
              <Selector
                name='gsc-days'
                value={days}
                choices={DAYS_CHOICES}
                onChange={(e) => setDays(Number(e.target.value))}
              />
              <AiButton loading={analyzing} disabled={!effectiveSite} onClick={handleAnalyze}>
                {analyzing ? '分析中' : '分析を開始する'}
              </AiButton>
            </ItemAlign>
          </FormRow>
        </AreaContent>
      </Area>

      {resultError && (
        <Area>
          <AreaContent>
            <Text size={14} color={TextColor.CRITICAL}>
              {resultError}
            </Text>
          </AreaContent>
        </Area>
      )}

      {result && (
        <>
          <div className='grid grid-cols-2 gap-4 lg:grid-cols-4'>
            <SummaryCard
              label='合計クリック数'
              value={result.summary.total_clicks.toLocaleString()}
            />
            <SummaryCard
              label='合計表示回数'
              value={result.summary.total_impressions.toLocaleString()}
            />
            <SummaryCard label='平均CTR' value={`${result.summary.avg_ctr}%`} />
            <SummaryCard label='平均掲載順位' value={`${result.summary.avg_position}`} />
          </div>

          <Area>
            <Heading>キーワード（{result.keyword_count}件）</Heading>
            <div className='flex items-center justify-end gap-2 px-2 py-1'>
              <TextField
                type='search'
                size={TextBoxSize.LARGE}
                value={kwFilter}
                placeholder='キーワードで検索する'
                onChange={(e) => setKwFilter(e.target.value)}
              />
            </div>
            <table className='w-full'>
              <thead>
                <TableRow head noBorder>
                  <TableHeadItem type='thin'>
                    <Text size={12} color={TextColor.SECONDARY}>
                      キーワード
                    </Text>
                  </TableHeadItem>
                  {['クリック数', '表示回数', 'CTR', '掲載順位'].map((h) => (
                    <TableHeadItem key={h} type='thin' width={88}>
                      <Text size={12} color={TextColor.SECONDARY}>
                        {h}
                      </Text>
                    </TableHeadItem>
                  ))}
                </TableRow>
              </thead>
              <tbody>
                {result.keywords
                  .filter((kw) => !kwFilter || kw.keyword.includes(kwFilter))
                  .map((kw, i) => (
                    <TableRow key={i}>
                      <TableRowItem>
                        <Badge badgeType={BadgeType.KEYWORD}>{kw.keyword}</Badge>
                      </TableRowItem>
                      <TableRowItem width={88}>
                        <Text>{kw.clicks.toLocaleString()}</Text>
                      </TableRowItem>
                      <TableRowItem width={88}>
                        <Text>{kw.impressions.toLocaleString()}</Text>
                      </TableRowItem>
                      <TableRowItem width={88}>
                        <Text color={ctrColor(kw.ctr)}>{kw.ctr}%</Text>
                      </TableRowItem>
                      <TableRowItem width={88}>
                        <Text color={positionColor(kw.position)}>{kw.position}</Text>
                      </TableRowItem>
                    </TableRow>
                  ))}
              </tbody>
            </table>
          </Area>

          <MetricTable
            title={`ページ別パフォーマンス（${result.page_count}件）`}
            firstLabel='ページURL'
            rows={result.pages.map((p) => ({
              key: p.page,
              clicks: p.clicks,
              impressions: p.impressions,
              ctr: p.ctr,
              position: p.position
            }))}
          />
        </>
      )}
    </div>
  )
}

export default MediaGscLinkagePanel
